import {
    ICustomDropdownTouchpoint,
    IRegisterCustomDropdownFunctionOptions,
} from '../../ScriptingEngineEnvironment/Touchpoint/CustomDropdown';
import DropdownOptionsRepository from '../../../DocumentEditRules/PluginHost/services/dropdownOptionsRepository';
import { IDocumentViewModel } from '../../../../services/Document/Api';
import { ExecutionContext } from '../../../../services/backendServices/ViewModels';
import { AxiosInstance } from 'axios';
import { ContextInformationCollector } from './ContextInformationCollector';
import { RuntimeRuleNonLibrary } from '../../CoreEngine/Utility/RuntimeRule';
import { PODocumentsForInvoice } from './extractPONumbers';

type RuleRegistration = {
    scriptName: string;
    dpsRuleName: RuntimeRuleNonLibrary['dpsRuleName'];
    configParams: RuntimeRuleNonLibrary['configParams'];
    ordinal: RuntimeRuleNonLibrary['ordinal'];
    customDropdownOptions: IRegisterCustomDropdownFunctionOptions[];
};
export class CustomDropdownTouchpoint implements ICustomDropdownTouchpoint {
    private registeredRules = new Map<string, RuleRegistration>();

    private currentScript: string | null = null;

    private hasCompletedRegistration = false;

    constructor(
        private readonly context: ExecutionContext,
        private api: () => AxiosInstance,
        private buyerCompanyId?: number,
        private senderCompanyId?: number,
        private parentCompanyId?: number, // for multi-company scenarios
        private writeToLog?: (message: string) => void,
        private dropdownOptionsRepository?: DropdownOptionsRepository
    ) {}

    setCurrentScript(rule: RuntimeRuleNonLibrary) {
        if (this.hasCompletedRegistration) {
            throw new Error('Registration has already been completed.');
        }

        this.currentScript = rule.name;

        this.registeredRules.set(rule.name, {
            scriptName: rule.name,
            dpsRuleName: rule.dpsRuleName,
            configParams: rule.configParams,
            ordinal: rule.ordinal,
            customDropdownOptions: [],
        });
    }

    completeRegistration() {
        this.currentScript = null;
        this.hasCompletedRegistration = true;
    }

    register(options: IRegisterCustomDropdownFunctionOptions[]): void {
        if (this.hasCompletedRegistration) {
            throw new Error('Tried to register a controller function after registration was completed.');
        }

        if (!this.currentScript) {
            throw new Error('Tried to register a controller function before setting a Script.');
        }

        const scriptConfig = this.registeredRules.get(this.currentScript)!;

        this.registeredRules.set(this.currentScript, {
            ...scriptConfig,
            customDropdownOptions: [...scriptConfig.customDropdownOptions, ...options],
        });
    }

    async executeCustomDropdownFunctions({
        document,
        contextInformationCollector,
        poDocumentsForInvoice,
    }: {
        document: IDocumentViewModel;
        contextInformationCollector: ContextInformationCollector;
        poDocumentsForInvoice: PODocumentsForInvoice;
    }) {
        if (!this.hasCompletedRegistration) {
            throw new Error('Tried to execute validation functions before registration was completed.');
        }

        if (this.registeredRules.size === 0) {
            this.writeToLog?.('No js scripts attached to mapping');
            return;
        }

        const customDropdownRegistrations = Array.from(this.registeredRules.values());

        // early return if there are no controller functions
        if (customDropdownRegistrations.every(({ customDropdownOptions }) => customDropdownOptions.length === 0)) {
            return;
        }

        const registrationsGroupedByOrdinal = customDropdownRegistrations.reduce((acc, registration) => {
            acc[registration.ordinal] ??= [];
            acc[registration.ordinal].push(registration);
            return acc;
        }, {} as Record<number, RuleRegistration[]>);

        const dropdownFetchGroups = Object.entries(registrationsGroupedByOrdinal).map(
            ([ordinal, dropdownRegistrations]) => {
                const groupExecution = async () => {
                    const dropdownFetchPromises = dropdownRegistrations.map(
                        async ({ scriptName, configParams, dpsRuleName, customDropdownOptions }) => {
                            const fetchPromises = customDropdownOptions.map(
                                async ({ fetchOptions, fieldDefinitionKey, shouldUpdate }) => {
                                    this.writeToLog?.(
                                        `Executing custom dropdown options function for script: ${scriptName}`
                                    );

                                    const previousDocumentState =
                                        contextInformationCollector.getContextInformation()?.previousExecution
                                            ?.document;

                                    const shouldUpdateOptions = !previousDocumentState
                                        ? true
                                        : shouldUpdate?.({
                                              oldState: previousDocumentState,
                                              newState: document,
                                          });

                                    if (!shouldUpdateOptions) {
                                        return;
                                    }

                                    const dropdownOptionValues = await fetchOptions({
                                        context: this.context,
                                        api: this.api,
                                        document,
                                        configParams,
                                        buyerCompanyId: this.buyerCompanyId,
                                        senderCompanyId: this.senderCompanyId,
                                        contextInformation: contextInformationCollector.getContextInformation(),
                                        parentCompanyId: this.parentCompanyId,
                                        dpsRuleName,
                                        poDocuments: poDocumentsForInvoice,
                                    });

                                    this.dropdownOptionsRepository?.setDropdownOptions(
                                        fieldDefinitionKey,
                                        dropdownOptionValues
                                    );

                                    this.writeToLog?.(
                                        `Finished executing custom dropdown fetch options function for script ${scriptName}`
                                    );
                                }
                            );

                            await Promise.all(fetchPromises);
                        }
                    );

                    await Promise.all(dropdownFetchPromises);
                };

                return [ordinal, groupExecution] as const;
            }
        );

        dropdownFetchGroups.sort((a, b) => Number(a[0]) - Number(b[0]));

        for await (const [ordinal, groupExecution] of dropdownFetchGroups) {
            this.writeToLog?.(`Executing custom dropdown fetch options for ordinal: ${ordinal}`);

            await groupExecution();

            this.writeToLog?.(`Finished executing custom dropdown fetch options for ordinal: ${ordinal}`);
        }
    }
}
